import { afterNextRender, ChangeDetectionStrategy, Component, ElementRef, Injector, OnDestroy, computed, inject, signal, viewChild } from '@angular/core';

// From the form's pre-filled link: https://docs.google.com/forms/d/e/<FORM_ID>/viewform?usp=pp_url&<MESSAGE_ENTRY>=...
const GOOGLE_FORM_ID = '1FAIpQLSeLS-HyfIZx7G8A_-WX8BnHu6WVtVI0CikPvD6gjt9QKK7Ejg';
const MESSAGE_ENTRY = 'entry.1183732912';
// Multiple-choice question whose options match the mood values below exactly
const MOOD_ENTRY = 'entry.1270309455';

const FORM_URL = `https://docs.google.com/forms/d/e/${GOOGLE_FORM_ID}/formResponse`;
const MAX_LENGTH = 1500;
const FOCUSABLE = 'button:not([disabled]), textarea, a[href], [tabindex]:not([tabindex="-1"])';

type Status = 'idle' | 'sending' | 'sent' | 'error';

interface Mood {
  emoji: string;
  label: string;
  value: string;
}

const MOODS: Mood[] = [
  { emoji: '🔥', label: 'Loved it', value: 'Loved It' },
  { emoji: '😐', label: 'It’s fine', value: "It's fine" },
  { emoji: '💀', label: 'Roast incoming', value: 'Roast incoming' }
];

@Component({
  selector: 'app-feedback',
  standalone: true,
  templateUrl: './feedback.component.html',
  styleUrls: ['./feedback.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeedbackComponent implements OnDestroy {
  readonly maxLength = MAX_LENGTH;
  readonly moods = MOODS;
  readonly isOpen = signal(false);
  readonly message = signal('');
  readonly mood = signal<string | null>(null);
  readonly status = signal<Status>('idle');
  // A mood on its own is enough; a message needs a few characters so stray keystrokes don't get sent
  readonly canSend = computed(() => (this.message().trim().length >= 3 || this.mood() !== null) && this.status() !== 'sending');

  private readonly modal = viewChild.required<ElementRef<HTMLElement>>('modal');
  private readonly panel = viewChild.required<ElementRef<HTMLElement>>('panel');
  private readonly field = viewChild<ElementRef<HTMLTextAreaElement>>('field');
  private readonly injector = inject(Injector);
  private returnFocus: HTMLElement | null = null;

  constructor() {
    // The contact card gets a translate during its scroll reveal, which would trap a fixed overlay inside it
    afterNextRender(() => document.body.appendChild(this.modal().nativeElement));
  }

  open(trigger: HTMLElement): void {
    this.returnFocus = trigger;
    if (this.status() !== 'sending') this.status.set('idle');
    this.isOpen.set(true);
    document.documentElement.style.overflow = 'hidden';
    afterNextRender(() => (this.field()?.nativeElement ?? this.panel().nativeElement).focus(), { injector: this.injector });
  }

  close(): void {
    if (!this.isOpen()) return;
    this.isOpen.set(false);
    document.documentElement.style.removeProperty('overflow');
    this.returnFocus?.focus({ preventScroll: true });
  }

  onInput(event: Event): void {
    this.message.set((event.target as HTMLTextAreaElement).value);
    if (this.status() === 'error') this.status.set('idle');
  }

  pickMood(value: string): void {
    this.mood.set(this.mood() === value ? null : value);
    if (this.status() === 'error') this.status.set('idle');
  }

  async submit(event: Event): Promise<void> {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    // Honeypot: real people never see this field, bots fill everything in. Fake success so bots move on.
    if ((form.elements.namedItem('website') as HTMLInputElement | null)?.value) {
      this.status.set('sent');
      return;
    }
    if (!this.canSend()) return;

    this.status.set('sending');
    const body = new URLSearchParams({ [MESSAGE_ENTRY]: this.message().trim() });
    const mood = this.mood();
    if (mood) body.set(MOOD_ENTRY, mood);
    try {
      // Google Forms sends no CORS headers, so the response is opaque; a network error is the only failure we can see
      await fetch(FORM_URL, {
        method: 'POST',
        mode: 'no-cors',
        body
      });
      this.message.set('');
      this.mood.set(null);
      this.status.set('sent');
    } catch {
      this.status.set('error');
    }
  }

  writeAnother(): void {
    this.status.set('idle');
    afterNextRender(() => this.field()?.nativeElement.focus(), { injector: this.injector });
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  ngOnDestroy(): void {
    this.modal().nativeElement.remove();
    document.documentElement.style.removeProperty('overflow');
  }

  private trapFocus(event: KeyboardEvent): void {
    const panel = this.panel().nativeElement;
    const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const current = document.activeElement;
    if (event.shiftKey && (current === first || current === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && current === last) {
      event.preventDefault();
      first.focus();
    }
  }
}
